// src/components/BundlerWalletManager.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Eye, EyeOff, Copy, Download, Wallet, Lock, Unlock, RefreshCw,
  Loader2, KeyRound, Check
} from 'lucide-react';
import { PublicKey } from '@solana/web3.js';
import { useWallet } from '../contexts/WalletContext';
import { bundlerService } from '../services/frontend-bundler/bundler.service';

// Make wallet fields optional to match service shape
interface WalletDetail {
  id?: string;
  address: string;
  funded?: boolean;
  active?: boolean;
  balance?: number;
  privateKeyPreview?: string;
  privateKey?: string;
}

// Allow Date coming from the service
interface Bundle {
  id: string;
  name: string;
  owner: string;
  totalBalance: number;
  createdAt?: string | Date;
  wallets: WalletDetail[];
}

const BundlerWalletManager: React.FC = () => {
  const { isConnected, publicKey } = useWallet();
  const owner = useMemo(() => (publicKey ? String(publicKey) : ''), [publicKey]);

  const [loading, setLoading] = useState(false);
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [selectedBundleId, setSelectedBundleId] = useState<string | null>(null);

  const [showKeys, setShowKeys] = useState(false);
  const [copiedTag, setCopiedTag] = useState<string | null>(null);

  // Unlock/Export modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordAction, setPasswordAction] = useState<'unlock' | 'export-with-keys'>('unlock');
  const [password, setPassword] = useState('');

  // Async actions
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawingProgress, setWithdrawingProgress] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const provider = (typeof window !== 'undefined' && (window as any).solana) || null;

  const selectedBundle = useMemo(
    () => bundles.find(b => b.id === selectedBundleId) || null,
    [bundles, selectedBundleId]
  );

  // Load bundles for connected wallet
  useEffect(() => {
    const load = async () => {
      if (!isConnected || !owner) {
        setBundles([]);
        setSelectedBundleId(null);
        return;
      }
      try {
        setLoading(true);
        const data = await bundlerService.getUserBundles(owner);
        // TS: data is structurally compatible with our Bundle (createdAt may be Date)
        setBundles(data as unknown as Bundle[]);
        if (data.length && !selectedBundleId) {
          setSelectedBundleId(data[0].id);
        }
      } catch (e) {
        console.error('Failed to load bundles:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isConnected, owner]); // re-run on wallet change

  const copy = async (text: string, tag: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedTag(tag);
      setTimeout(() => setCopiedTag(null), 1200);
    } catch {
      // ignore
    }
  };

  const refreshBalances = async () => {
    try {
      setRefreshing(true);
      await bundlerService.refreshBalances();
      if (owner) {
        const data = await bundlerService.getUserBundles(owner);
        setBundles(data as unknown as Bundle[]);
      }
    } catch (e) {
      console.error('Refresh balances failed:', e);
      alert('Failed to refresh balances');
    } finally {
      setRefreshing(false);
    }
  };

  const openUnlockModal = () => {
    if (!selectedBundle) return;
    setPasswordAction('unlock');
    setPassword('');
    setShowPasswordModal(true);
  };

  const openExportWithKeysModal = () => {
    if (!selectedBundle) return;
    setPasswordAction('export-with-keys');
    setPassword('');
    setShowPasswordModal(true);
  };

  const handlePasswordConfirm = async () => {
    if (!selectedBundle || !password) return;

    try {
      if (passwordAction === 'unlock') {
        const unlocked = await bundlerService.unlockBundle(selectedBundle.id, password);
        setBundles(prev =>
          (prev as Bundle[]).map(b => (b.id === unlocked.id ? (unlocked as unknown as Bundle) : b))
        );
        alert('🔓 Bundle unlocked!');
      } else if (passwordAction === 'export-with-keys') {
        const json = await bundlerService.exportBundle(selectedBundle.id, true, password);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bundle-${selectedBundle.id}-with-keys.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        alert('✅ Exported bundle with PRIVATE KEYS.\nKeep it secure.');
      }
    } catch (e: any) {
      alert(e?.message || 'Operation failed');
    } finally {
      setShowPasswordModal(false);
      setPassword('');
    }
  };

  const lockBundle = async () => {
    try {
      await bundlerService.lockBundle();
      if (owner) {
        const data = await bundlerService.getUserBundles(owner);
        setBundles(data as unknown as Bundle[]);
      }
      setShowKeys(false);
      alert('🔒 Bundle locked.');
    } catch (e: any) {
      alert(e?.message || 'Failed to lock');
    }
  };

  const exportBundleNoKeys = async () => {
    if (!selectedBundle) return;
    try {
      const json = await bundlerService.exportBundle(selectedBundle.id, false);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bundle-${selectedBundle.id}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      alert('✅ Exported bundle metadata (no private keys).');
    } catch (e: any) {
      alert(e?.message || 'Export failed');
    }
  };

  const withdrawAll = async () => {
    if (!selectedBundle) {
      alert('Select a bundle first.');
      return;
    }
    if (!isConnected || !publicKey) {
      alert('Please connect your wallet first');
      return;
    }
    if (!provider) {
      alert('Wallet provider not found');
      return;
    }
    try {
      setWithdrawing(true);
      setWithdrawingProgress(0);

      const mainWallet = new PublicKey(String(publicKey));
      const result = await bundlerService.withdrawAllFunds(
        mainWallet,
        (prog: number) => setWithdrawingProgress(Math.round(prog))
      );

      if (result.success) {
        alert(
          `✅ Withdrawal complete!\nTotal withdrawn: ${Number(
            result.totalWithdrawn
          ).toFixed(4)} SOL`
        );
        if (owner) {
          const data = await bundlerService.getUserBundles(owner);
          setBundles(data as unknown as Bundle[]);
        }
      } else {
        alert(`❌ Withdrawal failed: ${result.error || 'Unknown error'}`);
      }
    } catch (e: any) {
      console.error('Withdrawal error:', e);
      alert(`Withdrawal failed: ${e?.message || 'Unknown error'}`);
    } finally {
      setWithdrawing(false);
      setWithdrawingProgress(0);
    }
  };

  if (!isConnected) {
    return (
      <div className="bg-gray-900/50 backdrop-blur rounded-xl p-6 border border-gray-800">
        <p className="text-yellow-400">Connect your wallet to view your bundles.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-900/50 backdrop-blur rounded-xl p-6 border border-gray-800">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wallet className="text-purple-400" />
          <h2 className="text-xl font-semibold">My Bundles</h2>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={refreshBalances}
            disabled={refreshing}
            className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>

          <button
            onClick={() => setShowKeys(!showKeys)}
            className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded text-sm flex items-center gap-2"
          >
            {showKeys ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showKeys ? 'Hide Keys' : 'Show Keys'}
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-gray-400">
          <Loader2 className="animate-spin w-4 h-4" /> Loading bundles…
        </div>
      )}

      {!loading && bundles.length === 0 && (
        <p className="text-gray-400">No bundles yet. Create one in Tools → Bundler.</p>
      )}

      {bundles.length > 0 && (
        <>
          {/* Bundles picker */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {bundles.map((b) => (
              <button
                key={b.id}
                onClick={() => setSelectedBundleId(b.id)}
                className={`text-left p-3 rounded-lg border ${
                  selectedBundleId === b.id
                    ? 'border-purple-500 bg-purple-500/10'
                    : 'border-gray-800 bg-gray-800/40 hover:bg-gray-800/70'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium">{b.name || 'Untitled Bundle'}</div>
                  <div className="text-xs text-gray-400">{b.wallets?.length ?? 0} wallets</div>
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Balance: {Number(b.totalBalance || 0).toFixed(4)} SOL
                </div>
              </button>
            ))}
          </div>

          {/* Selected bundle details */}
          {selectedBundle && (
            <div className="bg-gray-800/40 rounded-lg border border-gray-800">
              <div className="flex items-center justify-between p-3 border-b border-gray-800">
                <div className="font-semibold">{selectedBundle.name}</div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={openUnlockModal}
                    className="px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-sm flex items-center gap-2"
                    title="Unlock bundle to reveal keys & enable withdrawals"
                  >
                    <Unlock className="w-4 h-4" />
                    Unlock
                  </button>
                  <button
                    onClick={lockBundle}
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-sm flex items-center gap-2"
                    title="Forget decrypted keys from memory"
                  >
                    <Lock className="w-4 h-4" />
                    Lock
                  </button>
                  <button
                    onClick={exportBundleNoKeys}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm flex items-center gap-2"
                    title="Export (no private keys)"
                  >
                    <Download className="w-4 h-4" />
                    Export
                  </button>
                  <button
                    onClick={openExportWithKeysModal}
                    className="px-3 py-1 bg-purple-600 hover:bg-purple-700 rounded text-sm flex items-center gap-2"
                    title="Export WITH private keys (requires password)"
                  >
                    <KeyRound className="w-4 h-4" />
                    Export + Keys
                  </button>
                  <button
                    onClick={withdrawAll}
                    disabled={withdrawing}
                    className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 rounded text-sm flex items-center gap-2 disabled:opacity-50"
                    title="Withdraw all SOL from unlocked bundle wallets"
                  >
                    {withdrawing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
                    {withdrawing ? `Withdrawing… ${withdrawingProgress}%` : 'Withdraw All'}
                  </button>
                </div>
              </div>

              {/* Wallets table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-gray-800">
                      <th className="py-2 px-3">Address</th>
                      <th className="py-2 px-3">Balance (SOL)</th>
                      <th className="py-2 px-3">Funded</th>
                      <th className="py-2 px-3">Key</th>
                      <th className="py-2 px-3">Copy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBundle.wallets.map((w) => {
                      const tagAddr = `addr:${w.address}`;
                      const tagKey = `key:${w.address}`;
                      return (
                        <tr key={w.id || w.address} className="border-b border-gray-800/50">
                          <td className="py-2 px-3 font-mono">
                            {w.address.slice(0, 4)}…{w.address.slice(-4)}
                          </td>
                          <td className="py-2 px-3">{Number(w.balance ?? 0).toFixed(4)}</td>
                          <td className="py-2 px-3">{w.funded ? 'Yes' : 'No'}</td>
                          <td className="py-2 px-3">
                            {showKeys ? (
                              <span className="text-purple-300">
                                {w.privateKey
                                  ? `${w.privateKey.slice(0, 8)}…${w.privateKey.slice(-6)}`
                                  : w.privateKeyPreview || '•••'}
                              </span>
                            ) : (
                              '•••'
                            )}
                          </td>
                          <td className="py-2 px-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => copy(w.address, tagAddr)}
                                className="px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded"
                                title="Copy address"
                              >
                                {copiedTag === tagAddr ? (
                                  <span className="inline-flex items-center gap-1">
                                    <Check className="w-3 h-3" /> Copied
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1">
                                    <Copy className="w-3 h-3" /> Address
                                  </span>
                                )}
                              </button>

                              {showKeys && w.privateKey && (
                                <button
                                  onClick={() => copy(w.privateKey as string, tagKey)}
                                  className="px-2 py-1 bg-red-700 hover:bg-red-600 rounded"
                                  title="Copy full private key"
                                >
                                  {copiedTag === tagKey ? (
                                    <span className="inline-flex items-center gap-1">
                                      <Check className="w-3 h-3" /> Copied
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1">
                                      <Copy className="w-3 h-3" /> Key
                                    </span>
                                  )}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-4 gap-4 p-3">
                <div className="bg-gray-900/50 rounded-lg p-3">
                  <div className="text-sm text-gray-400">Total Wallets</div>
                  <div className="text-xl font-bold">{selectedBundle.wallets.length}</div>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-3">
                  <div className="text-sm text-gray-400">Funded</div>
                  <div className="text-xl font-bold text-green-400">
                    {selectedBundle.wallets.filter(w => w.funded).length}
                  </div>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-3">
                  <div className="text-sm text-gray-400">Active</div>
                  <div className="text-xl font-bold text-blue-400">
                    {selectedBundle.wallets.filter(w => w.active).length}
                  </div>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-3">
                  <div className="text-sm text-gray-400">Total Balance</div>
                  <div className="text-xl font-bold text-purple-400">
                    {Number(selectedBundle.totalBalance || 0).toFixed(4)} SOL
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-900 p-6 rounded-xl max-w-md w-full border border-gray-800">
            <h3 className="text-xl font-bold mb-4">
              {passwordAction === 'unlock' ? 'Unlock Bundle' : 'Export + Private Keys'}
            </h3>

            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-gray-800 rounded px-3 py-2"
                placeholder="Enter your bundle password"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handlePasswordConfirm}
                className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 rounded"
              >
                {passwordAction === 'unlock' ? 'Unlock' : 'Export'}
              </button>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPassword('');
                }}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default BundlerWalletManager;
